import { AsyncPipe, DatePipe, NgStyle } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import {
  MatDatepickerInputEvent,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ExternalResource, isError, isLoaded, isLoading } from '@next/arable-lib/external-resource';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmptyWidgetComponent } from 'src/app/shared/components/empty-widget.component';
import { LoadingCardComponent } from 'src/app/shared/components/loading-card.component/loading-card.component';
import { toLocaleSignal } from 'src/app/shared/utils';
import { SatelliteImage } from '../../shared/models/models';
import { NDVI_GRADIENT } from '../../shared/constants/tga.constants';
import { ImagePopUpDialogComponent, ImagePopUpDialogData } from '../dialogs/image-pop-up-dialog/image-pop-up-dialog.component';
import { DataUriPipe } from '../../shared/pipes/data-uri.pipe';

@Component({
  selector: 'app-tga-satellite-images',
  standalone: true,
  templateUrl: './tga-satellite-images.component.html',
  styleUrls: ['./tga-satellite-images.component.scss'],
  imports: [
    AsyncPipe,
    TranslatePipe,
    DatePipe,
    NgStyle,
    EmptyWidgetComponent,
    LoadingCardComponent,
    MatFormField,
    FormsModule,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDateRangePicker,
    MatIconButton,
    MatIcon,
    DataUriPipe,
  ],
})
export class TgaSatelliteImagesComponent {
  private readonly localeService = inject(LocaleService);
  private readonly matDialog = inject(MatDialog);

  satelliteImages$ = input<Observable<ExternalResource<SatelliteImage[]>>>(of({ state: 'idle' }));
  cropFieldId = input<number | null>(null);
  cropFieldName = input<string>('');

  protected readonly isLoading = isLoading;
  protected readonly isLoaded = isLoaded;
  protected readonly isError = isError;

  // Date range state (Signal-based)
  protected readonly startDate = signal<Date | null>(null);
  protected readonly endDate = signal<Date | null>(null);

  // Output event for date range changes
  readonly dateRangeChanged = output<{ startDate: Date; endDate: Date }>();

  // Date constraints
  protected readonly minDate = computed(() => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return oneYearAgo;
  });

  protected readonly maxDate = computed(() => new Date());

  // Locale signal for date formatting
  protected readonly locale = toLocaleSignal(this.localeService);

  constructor() {
    this.initializeDefaultDateRange();
  }

  readonly sortedSatelliteImages$ = computed(() =>
    this.satelliteImages$().pipe(
      map((resource) => {
        if (isLoaded(resource) && resource.data) {
          return {
            ...resource,
            data: [...resource.data].sort((a, b) => new Date(a.imageDate).getTime() - new Date(b.imageDate).getTime()),
          };
        }
        return resource;
      }),
    ),
  );

  readonly gradientStyle = computed(() => ({
    background: NDVI_GRADIENT,
  }));

  protected readonly emptyMessage = computed(() => {
    const start = this.startDate();
    const end = this.endDate();
    const currentLocale = this.locale();

    if (!start || !end) {
      return this.localeService.translate('TGA.Advice.Cultivation.SatelliteImages.Empty');
    }
    const datePipe = new DatePipe(currentLocale);
    const startDateStr = datePipe.transform(start, 'mediumDate', undefined, currentLocale) || '';
    const endDateStr = datePipe.transform(end, 'mediumDate', undefined, currentLocale) || '';

    return this.localeService.translate('TGA.Advice.Cultivation.SatelliteImages.EmptyWithRange', {
      startDate: startDateStr,
      endDate: endDateStr,
    });
  });

  protected onStartDateChange(event: MatDatepickerInputEvent<Date>): void {
    const newStartDate = event.value;
    this.startDate.set(newStartDate);
  }

  protected onEndDateChange(event: MatDatepickerInputEvent<Date>): void {
    const newEndDate = event.value;
    this.endDate.set(newEndDate);
  }

  protected onDatePickerClosed(): void {
    const currentStartDate = this.startDate();
    const currentEndDate = this.endDate();

    if (currentStartDate && currentEndDate) {
      this.dateRangeChanged.emit({ startDate: currentStartDate, endDate: currentEndDate });
    }
  }

  protected onImageClicked(images: SatelliteImage[], index: number): void {
    const titleBase = this.localeService.translate('TGA.Advice.Cultivation.SatelliteImages.Dialog.Title');
    const name = this.cropFieldName();
    const title = name ? `${titleBase} — ${name}` : titleBase;

    const dialogData: ImagePopUpDialogData = {
      title,
      images,
      initialIndex: index,
      cropFieldId: this.cropFieldId() ?? 0,
      minDate: this.minDate(),
      maxDate: this.maxDate(),
      initialFetchFromDate: this.startDate()?.toISOString(),
      confirmButton: false,
      cancelButton: false,
    };

    this.matDialog.open(ImagePopUpDialogComponent, {
      data: dialogData,
      disableClose: false,
      width: '96vw',
      maxWidth: '1344px',
      height: '88vh',
      maxHeight: '88vh',
    });
  }

  private initializeDefaultDateRange(): void {
    const today = new Date();
    const twentyNineDaysAgo = new Date();
    twentyNineDaysAgo.setDate(today.getDate() - 29);

    this.startDate.set(twentyNineDaysAgo);
    this.endDate.set(today);
  }
}
